import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { MapPin, Truck, ShieldCheck, Clock, ArrowRight, Phone, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { DEPARTEMENTS, TARIFS, PRESTATIONS } from '../data/departements';
import Footer from './Footer';

const JSONLD_ID = 'jsonld-departement';

const buildJsonLd = (d) => [
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: `Location de benne ${d.dans}`,
    serviceType: 'Location de benne de chantier',
    provider: { '@type': 'LocalBusiness', name: 'Location Benne Chantier', telephone: '+33745558835', url: 'https://www.locationbennechantier.com', image: 'https://www.locationbennechantier.com/logo-ler-square.png', logo: 'https://www.locationbennechantier.com/logo-ler-square.png' },
    areaServed: { '@type': 'AdministrativeArea', name: `${d.nom} (${d.code})` },
    url: d.url,
    offers: { '@type': 'Offer', priceCurrency: 'EUR', price: '245', description: 'Benne 3m³ gravats, livraison et traitement inclus' },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: d.faq.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://www.locationbennechantier.com/' },
      { '@type': 'ListItem', position: 2, name: `Location benne ${d.nom} (${d.code})`, item: d.url },
    ],
  },
];

const DepartementPage = ({ dept }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const autres = DEPARTEMENTS.filter((d) => d.code !== dept.code);
  const orderPath = user ? '/mon-compte' : '/commander-sans-compte';

  useEffect(() => {
    window.scrollTo(0, 0);
    let script = document.getElementById(JSONLD_ID);
    if (!script) {
      script = document.createElement('script');
      script.id = JSONLD_ID;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(buildJsonLd(dept));
    return () => document.getElementById(JSONLD_ID)?.remove();
  }, [dept]);

  return (
    <div className="pt-24 bg-white" data-testid={`dept-page-${dept.code}`}>
      <section className="bg-gradient-to-br from-green-50 via-white to-green-50 py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="text-sm text-gray-500 mb-6" aria-label="Fil d'Ariane" data-testid="dept-breadcrumb">
            <Link to="/" className="hover:text-green-600">Accueil</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-800">Location benne {dept.nom} ({dept.code})</span>
          </nav>
          <div className="grid lg:grid-cols-5 gap-10 items-start">
            <div className="lg:col-span-3 space-y-6">
              <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                <MapPin className="h-3.5 w-3.5 mr-1" /> Département {dept.code}
              </Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight" data-testid="dept-h1">
                Location de benne <span className="text-green-600">{dept.dans}</span>
              </h1>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed">{dept.intro}</p>
              <div className="flex flex-wrap gap-3">
                {[{ icon: Clock, t: 'Livraison 24h' }, { icon: ShieldCheck, t: 'Paiement sécurisé' }, { icon: Truck, t: 'Dès 245€ HT' }].map((b) => (
                  <span key={b.t} className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm text-sm font-medium text-gray-700">
                    <b.icon className="h-4 w-4 text-green-600" /> {b.t}
                  </span>
                ))}
              </div>
            </div>
            <Card className="lg:col-span-2 p-6 shadow-xl border-2 border-green-100">
              <h2 className="text-lg font-bold text-gray-900 mb-2">Commandez votre benne {dept.dans}</h2>
              <p className="text-sm text-gray-600 mb-5">Devis instantané, livraison sous 24h, paiement par carte.</p>
              <Button onClick={() => navigate(orderPath)} className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg font-bold" data-testid="dept-order-btn">
                Commander sans compte <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              {!user && (
                <Button onClick={() => navigate('/inscription')} variant="outline" className="w-full mt-3 border-2 border-green-600 text-green-700 hover:bg-green-50 font-bold" data-testid="dept-signup-btn">
                  Créer un compte professionnel
                </Button>
              )}
              <a href="tel:0745558835" className="mt-5 flex items-center justify-center gap-2 text-green-700 font-semibold hover:text-green-800" data-testid="dept-phone-link">
                <Phone className="h-4 w-4" /> 07 45 55 88 35
              </a>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-base md:text-lg font-bold text-gray-900 uppercase tracking-wide mb-4">Zone desservie {dept.dans}</h2>
          <p className="text-gray-600 mb-6">Nous livrons dans toutes les communes du département, notamment :</p>
          <ul className="flex flex-wrap gap-2" data-testid="dept-villes">
            {dept.villes.map((v) => (
              <li key={v} className="px-3 py-1.5 rounded-full bg-gray-100 text-gray-700 text-sm">{v}</li>
            ))}
          </ul>
          <p className="mt-6 text-gray-700 bg-green-50 border-l-4 border-green-500 p-4 rounded-r-lg">{dept.atout}</p>
        </div>
      </section>

      <section className="py-14 bg-gray-950 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-base md:text-lg font-bold uppercase tracking-wide mb-2">Conseils pratiques {dept.dans}</h2>
          <p className="text-sm text-gray-400 mb-6">Issus de nos conditions de location et des démarches à prévoir avant la livraison.</p>
          <div className="grid md:grid-cols-3 gap-4" data-testid="dept-conseils">
            {dept.conseils.map((c, i) => (
              <div key={i} className="p-5 rounded-xl bg-white/5 border border-white/10">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-green-600 text-xs font-bold mb-3">{i + 1}</span>
                <p className="text-sm text-gray-200 leading-relaxed">{c}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-base md:text-lg font-bold text-gray-900 uppercase tracking-wide mb-6">Tarifs location de benne {dept.dans}</h2>
          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
            <table className="w-full text-sm" data-testid="dept-tarifs-table">
              <thead className="bg-gray-900 text-white">
                <tr>
                  <th className="text-left px-4 py-3">Volume</th>
                  <th className="text-left px-4 py-3">Gravats / Terre</th>
                  <th className="text-left px-4 py-3">Mélange (DIB)</th>
                </tr>
              </thead>
              <tbody>
                {TARIFS.map((t) => (
                  <tr key={t.volume} className="border-t border-gray-100">
                    <td className="px-4 py-3 font-bold text-gray-900">Benne {t.volume}</td>
                    <td className="px-4 py-3">
                      {t.gravats ? <><span className="font-semibold text-green-700">{t.gravats[0]}€ HT</span> <span className="text-gray-500">· {t.gravats[1]}€ TTC</span></> : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      {t.melange ? <><span className="font-semibold text-green-700">{t.melange[0]}€ HT</span> <span className="text-gray-500">· {t.melange[1]}€ TTC</span></> : <span className="text-gray-400">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500 mt-3">Forfait 15 jours calendaires incluant livraison, retrait et traitement. Même tarif HT pour professionnels et particuliers ; TVA de 20 % ajoutée au total à payer. Suppléments définis : Livraison Express +100€ HT, passage à vide 180€ HT, immobilisation prolongée 100€ HT par 15 jours.</p>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-base md:text-lg font-bold text-gray-900 uppercase tracking-wide mb-6">Nos prestations {dept.dans}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {PRESTATIONS.map((p) => (
              <Card key={p.label} className="p-5 border-gray-100 hover:border-green-200 transition-colors">
                <CheckCircle className="h-5 w-5 text-green-600 mb-3" />
                <h3 className="font-bold text-gray-900 mb-1">{p.label}</h3>
                <p className="text-sm text-gray-600">{p.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-base md:text-lg font-bold text-gray-900 uppercase tracking-wide mb-6">Questions fréquentes – benne {dept.dans}</h2>
          <Card className="p-6 border-0 shadow-md">
            <Accordion type="single" collapsible className="w-full" data-testid="dept-faq">
              {dept.faq.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left hover:text-green-600"><span className="font-semibold text-gray-900">{f.q}</span></AccordionTrigger>
                  <AccordionContent className="text-gray-600 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Card>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-base md:text-lg font-bold text-gray-900 uppercase tracking-wide mb-6">Location de benne dans les autres départements</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="dept-autres">
            {autres.map((d) => (
              <Link key={d.code} to={d.path} className="group flex items-center justify-between px-4 py-3 rounded-lg border border-gray-200 hover:border-green-500 hover:bg-green-50 transition-colors">
                <span className="text-sm font-medium text-gray-800">{d.nom} ({d.code})</span>
                <ArrowRight className="h-4 w-4 text-gray-400 group-hover:text-green-600" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DepartementPage;
